import { useEffect, useRef } from 'react';
import { TIMER_TIMESTAMPS } from './constants';
import fetchServerTime, {
  getServerTimeRetryDelayMs,
  isServerTimeFetchEnabled,
  isServerTimeSynced,
} from '@/api/aggregation/time/get-server-time';

import { setServerTimeValue, setSessionStartValue } from '@/store/actions.ts';
import { useStore } from '@/store/useStore';

const applyServerTimeResult = (timeMs: number) => {
  setServerTimeValue(timeMs);
  globalThis?.performance?.clearMarks?.(TIMER_TIMESTAMPS.SERVER_MARK);
  globalThis?.performance?.mark?.(TIMER_TIMESTAMPS.SERVER_MARK);
};

/**
 * Keeps requesting server time until aggregation returns a valid datetime.
 * Failed/throttled attempts still update the store with client time, but
 * retries continue at a capped rate (1/s, then 1/5s) so recovery is detected.
 */
export const useServerTimeInitialization = () => {
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clearRetryTimeout = () => {
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const scheduleRetry = () => {
      clearRetryTimeout();
      if (cancelled || !isServerTimeFetchEnabled() || isServerTimeSynced()) {
        return;
      }

      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        void runSync();
      }, getServerTimeRetryDelayMs());
    };

    const runSync = async () => {
      if (cancelled) {
        return;
      }

      const result = await fetchServerTime();
      if (cancelled) {
        return;
      }

      applyServerTimeResult(result.timeMs);

      if (useStore.getState().sessionStartValue === undefined) {
        setSessionStartValue(Date.now());
      }

      if (!result.fromServer) {
        scheduleRetry();
      }
    };

    const { serverTimeValue } = useStore.getState();
    if (!(isServerTimeSynced() && serverTimeValue !== undefined)) {
      void runSync();
    }

    const handleVisibilityChange = () => {
      if (document?.visibilityState !== 'visible' || cancelled) {
        return;
      }

      void fetchServerTime().then((result) => {
        if (cancelled) {
          return;
        }

        applyServerTimeResult(result.timeMs);
        if (!result.fromServer) {
          scheduleRetry();
        }
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearRetryTimeout();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      globalThis?.performance?.clearMarks?.(TIMER_TIMESTAMPS.SERVER_MARK);
    };
  }, [hasActiveSession]);
};
